import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Checkbox, Collapse, IconButton, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchOff from "@mui/icons-material/SearchOff";
import ClearIcon from "@mui/icons-material/Clear";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { AbsCheckBox, Button } from "abs-ui-components";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { getUKETSAllShips, getUKETSAllEligibleShipIds, saveUKETSAerReports } from "../../../services/UKETSAerService";
import ShipLevelDrawer from "./ShipLevelDrawer";
import { ClaimType } from "../../../constants/claims";
import { useContext } from "react";
import { ClaimsContext } from "../../../services/ClaimsService";

const dash = (val) => (val !== null && val !== undefined && val !== "" ? val : "");

/** Round a numeric value to 3 decimal places; returns empty string for null/empty */
const round3 = (val) => {
	if (val === null || val === undefined || val === "") return "";
	const num = parseFloat(val);
	 const result = isNaN(num) ? val : num.toFixed(3);
	 console.log(`round3: input=${val}, parsed=${num}, result=${result}`);
	 return result;
};

const vesselKey = (row) => `${row.imoNumber || ""}`;

// ---------------------------------------------------------------------------
// Global Filter Text Field 
// ---------------------------------------------------------------------------
const GlobalFilterTextField = React.forwardRef(({ searchInput, applyGlobalSearch, autoFocus }, ref) => {
	const [value, setValue] = useState(searchInput ?? "");

	const inputRef = useRef(null);

	useEffect(() => {
		setValue(searchInput ?? "");
	}, [searchInput]);

	useImperativeHandle(ref, () => ({
		getValue: () => value,
		clear: () => setValue(""),
		focus: () => inputRef.current?.focus(),
	}));

	return (
		<TextField
			inputRef={inputRef}
			autoFocus={autoFocus}
			size="small"
			variant="outlined"
			placeholder="Type and Search"
			value={value}
			onChange={(e) => {
				const newValue = e.target.value;
				setValue(newValue);
				if (newValue === "" && searchInput) {
					applyGlobalSearch("");
				}
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					applyGlobalSearch(value);
				}
			}}
			InputProps={{
				startAdornment: (
					<InputAdornment position="start">
						<SearchIcon fontSize="small" />
					</InputAdornment>
				),
				endAdornment: value && (
					<InputAdornment position="end">
						<IconButton
							size="small"
							onClick={() => {
								setValue("");
								applyGlobalSearch("");
							}}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</InputAdornment>
				),
			}}
		/>
	);
});

GlobalFilterTextField.displayName = "GlobalFilterTextField";

// ---------------------------------------------------------------------------
// All Ships Table Columns

const ALL_SHIPS_COLUMNS = [
	{
		accessorKey: "sNo",
		header: "S.No.",
		size: 60,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ row }) => row.index + 1,
	},
	{
		accessorKey: "vesselName",
		header: "Vessel Name",
		size: 200,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{cell.getValue() || ""}</span>,
	},
	{
		accessorKey: "imoNumber",
		header: "IMO Number",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
	{
		accessorKey: "flagState",
		header: "Flag State",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
	{
		accessorKey: "shipType",
		header: "Ship Type",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
	{
		accessorKey: "grossTonnage",
		header: (
			<>
				Gross Tonnage
				<br />
				(GT)
			</>
		),
		size: 90,
		enableColumnActions: false,
		enableColumnFilters: false,
		muiTableHeadCellProps: {
			sx: {
				whiteSpace: "normal",
				wordBreak: "break-word",
				lineHeight: "1.3",
			},
		},
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},
	{
		accessorKey: "iceClass",
		header: "Ice Class",
		size: 70,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
	{
		accessorKey: "reportingPeriod",
		header: "Reporting Period",
		size: 70,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
	{
		accessorKey: "totalFuelConsumption",
		header: "Total Fuel Consumption",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},
	{
		accessorKey: "totalUkEtsConsumption",
		header: (
			<>
				Total UK
				<br />
				ETS Emissions
				<br />
				(ME ETS)
			</>
		),
		size: 130,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},
	{
		accessorKey: "materialityLevel",
		header: "Materiality Level (%)",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},
	{
		accessorKey: "shipLevelErId",
		header: "Ship Level ER ID",
		size: 220,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
	{
		accessorKey: "shipLevelErStatus",
		header: "Ship Level ER Verification Status",
		size: 220,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},
];

// ---------------------------------------------------------------------------
// Ships in AER Table Columns
// ---------------------------------------------------------------------------
const buildAerColumns = (selectedShipMap, onToggleSelect, isEditable, onViewShip) => [
	{
		accessorKey: "add",
		header: "ADD",
		size: 60,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,

		Cell: ({ row: { original } }) => {
			const key = vesselKey(original);

			const isChecked = selectedShipMap[key] === original.reportMstId;

			return (
				<AbsCheckBox
					checked={isChecked}
					disabled={!isEditable}
					handleChange={() => onToggleSelect(original)}
					id={`ship-checkbox-${original.reportMstId}`}
				/>
			);
		},
	},

	{
		accessorKey: "sNo",
		header: "S.No.",
		size: 60,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ row }) => row.index + 1,
	},

	{
		accessorKey: "vesselName",
		header: "Vessel Name",
		size: 200,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{cell.getValue() || ""}</span>,
	},

	{
		accessorKey: "imoNumber",
		header: "IMO Number",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "flagState",
		header: "Flag State",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "shipType",
		header: "Ship Type",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "grossTonnage",
		header: (
			<>
				Gross Tonnage
				<br />
				(GT)
			</>
		),
		size: 90,
		minSize: 90,
		maxSize: 90,
		enableColumnActions: false,
		enableColumnFilters: false,
		muiTableHeadCellProps: {
			sx: {
				whiteSpace: "normal",
				wordBreak: "break-word",
				lineHeight: "1.3",
			},
		},
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},

	{
		accessorKey: "iceClass",
		header: "Ice Class",
		size: 70,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "reportingPeriod",
		header: "Reporting Period",
		size: 60,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "totalFuelConsumption",
		header: "Total Fuel Consumption",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},

	{
		accessorKey: "totalUkEtsConsumption",
		header: (
			<>
				Total UK
				<br />
				ETS Emissions
				<br />
				(ME ETS)
			</>
		),
		size: 130,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},

	{
		accessorKey: "materialityLevel",
		header: "Materiality Level (%)",
		size: 110,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{round3(cell.getValue())}</span>,
	},

	{
		accessorKey: "shipLevelErId",
		header: "Ship Level ER ID",
		size: 220,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "shipLevelErStatus",
		header: "Ship Level ER Verification Status",
		size: 220,
		enableColumnActions: false,
		enableColumnFilters: false,
		Cell: ({ cell }) => <span>{dash(cell.getValue())}</span>,
	},

	{
		accessorKey: "reportView",
		header: "Report View",
		size: 100,
		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,

		Cell: ({ row: { original } }) => (
			<Button
				variant="tertiary"
				onClick={() => {
					onViewShip(original);
				}}
			>
				View
			</Button>
		),
	},
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const UKETSListOfShips = ({ ukEtsAerMstId, isEditable, status }) => {
	const [allShips, setAllShips] = useState([]);
	const [verifiedShips, setVerifiedShips] = useState([]);

	const [allShipsLoading, setAllShipsLoading] = useState(true);
	const [eligibleShipsLoading, setEligibleShipsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isSelectingAll, setIsSelectingAll] = useState(false);

	// -------------------------------------------------------------------------
	// Server-side pagination state
	// -------------------------------------------------------------------------
	const [allShipsRowCount, setAllShipsRowCount] = useState(0);
	const [eligibleShipsRowCount, setEligibleShipsRowCount] = useState(0);

	const [allShipsPagination, setAllShipsPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});

	const [eligibleShipsPagination, setEligibleShipsPagination] = useState({
		pageIndex: 0,
		pageSize: 25,
	});

	// { [vessel + WCN]: reportMstId }
	const [selectedShipMap, setSelectedShipMap] = useState({});

	// True only during the very first eligible-ships load (drawer open / page
	// mount). Subsequent page / filter changes must NOT reset prior selections.
	const isInitialEligibleLoad = useRef(true);

	// Warning: IMO numbers of eligible ships that have not been selected
	const [unselectedWarning, setUnselectedWarning] = useState(null);

	// Applied global filter values — changing these triggers the API
	const [allShipsGlobalFilter, setAllShipsGlobalFilter] = useState("");
	const [eligibleShipsGlobalFilter, setEligibleShipsGlobalFilter] = useState("");

	// Input display values
	const [allShipsSearchInput, setAllShipsSearchInput] = useState("");
	const [eligibleShipsSearchInput, setEligibleShipsSearchInput] = useState("");

	const allShipsSearchRef = useRef(null);
	const eligibleShipsSearchRef = useRef(null);

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedShipForDrawer, setSelectedShipForDrawer] = useState(null);

	const [showAllShipsSearch, setShowAllShipsSearch] = useState(false);
	const [showEligibleShipsSearch, setShowEligibleShipsSearch] = useState(false);

	const { userHasClaim } = useContext(ClaimsContext);
	const SubmittedToAbs = (status === "No Data Submitted" || status === "Under Revision") && userHasClaim(ClaimType.SUBMIT_REPORT);

	const SentToCompany = !SubmittedToAbs;
	// -------------------------------------------------------------------------
	// Helpers — build per-table payload section
	// -------------------------------------------------------------------------
	const buildTablePayload = (globalFilter, pagination = { pageIndex: 0, pageSize: 25 }) => ({
		globalFilter: globalFilter ?? "",
		filters: [],
		pagination: {
			page: pagination.pageIndex + 1,
			pageSize: pagination.pageSize,
		},
		sortOption: {
			sortField: "",
			isAscending: true,
		},
		reportYear: 0,
	});

	// -------------------------------------------------------------------------
	// Load All Ships
	// -------------------------------------------------------------------------
	const loadAllShips = useCallback(
		async (globalFilter = "", pagination = allShipsPagination) => {
			setAllShipsLoading(true);

			try {
				const payload = {
					listOfShips: buildTablePayload(globalFilter, pagination),

					eligibleShips: buildTablePayload(""),
				};

				const response = await getUKETSAllShips(ukEtsAerMstId, payload);

				setAllShips(response?.listOfShips?.data ?? []);

				setAllShipsRowCount(response?.listOfShips?.pagination?.totalItems ?? 0);
			} catch {
				emitToasterMsg("error", "Error", "Failed to load ships data.");
			} finally {
				setAllShipsLoading(false);
			}
		},
		[ukEtsAerMstId, allShipsPagination]
	);

	// -------------------------------------------------------------------------
	// Load Eligible / Ships-in-AER
	// -------------------------------------------------------------------------
	const loadEligibleShips = useCallback(
		async (globalFilter = "", pagination = eligibleShipsPagination) => {
			setEligibleShipsLoading(true);

			try {
				const payload = {
					listOfShips: buildTablePayload(""),
					eligibleShips: buildTablePayload(globalFilter, pagination),
				};

				const response = await getUKETSAllShips(ukEtsAerMstId, payload);

				const eligibleShips = response?.eligibleShips?.data ?? [];
				const reportMstIds = response?.reportMstIds ?? [];

				setSelectedShipMap((prev) => {
					if (isInitialEligibleLoad.current) {
						isInitialEligibleLoad.current = false;
						const seededMap = { ...prev };
						eligibleShips.forEach((ship) => {
							if (reportMstIds.includes(ship.reportMstId)) {
								seededMap[vesselKey(ship)] = ship.reportMstId;
							}
						});
						return seededMap;
					}

					const updatedMap = { ...prev };
					eligibleShips.forEach((ship) => {
						const key = vesselKey(ship);
						if (reportMstIds.includes(ship.reportMstId) && !(key in updatedMap)) {
							updatedMap[key] = ship.reportMstId;
						}
					});
					return updatedMap;
				});

				setEligibleShipsRowCount(response?.eligibleShips?.pagination?.totalItems ?? 0);

				let shipsInAer = [];

				if (SubmittedToAbs && !SentToCompany) {
					shipsInAer = eligibleShips;
				} else {
					const listSnapshot = globalFilter ? [] : allShips;

					const verificationCompletedShips = listSnapshot.filter((ship) => ship.shipLevelErStatus === "Verification Completed");

					const existingKeys = new Set(eligibleShips.map((ship) => ship.reportMstId));

					const additionalVerifiedShips = verificationCompletedShips.filter((ship) => !existingKeys.has(ship.reportMstId));

					shipsInAer = [...eligibleShips, ...additionalVerifiedShips];
				}

				setVerifiedShips(shipsInAer);
			} catch {
				emitToasterMsg("error", "Error", "Failed to load ships data.");
			} finally {
				setEligibleShipsLoading(false);
			}
		},
		[ukEtsAerMstId, allShips, eligibleShipsPagination]
	);

	// -------------------------------------------------------------------------
	// Initial load
	// -------------------------------------------------------------------------
	// useEffect(() => {
	// 	if (ukEtsAerMstId) {
	// 		isInitialEligibleLoad.current = true;
	// 		loadAllShips("");
	// 		loadEligibleShips("");
	// 	}
	// }, [ukEtsAerMstId]);

	// -------------------------------------------------------------------------
	// Re-fetch All Ships when search / pagination changes
	// -------------------------------------------------------------------------
	useEffect(() => {
		if (ukEtsAerMstId) {
			loadAllShips(allShipsGlobalFilter, allShipsPagination);
		}

	}, [ukEtsAerMstId, allShipsGlobalFilter, allShipsPagination]);

	// -------------------------------------------------------------------------
	// Re-fetch Eligible Ships when search / pagination changes
	// -------------------------------------------------------------------------
	useEffect(() => {
		if (ukEtsAerMstId) {
			loadEligibleShips(eligibleShipsGlobalFilter, eligibleShipsPagination);
		}
	}, [ukEtsAerMstId, eligibleShipsGlobalFilter, eligibleShipsPagination]);

	// -------------------------------------------------------------------------
	// Apply search helpers
	// -------------------------------------------------------------------------
	const applyAllShipsSearch = useCallback((value) => {
		setAllShipsSearchInput(value);
		setAllShipsGlobalFilter(value);

		// Search should always start from page 1
		setAllShipsPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));
	}, []);

	const applyEligibleShipsSearch = useCallback((value) => {
		setEligibleShipsSearchInput(value);
		setEligibleShipsGlobalFilter(value);

		// Search should always start from page 1
		setEligibleShipsPagination((prev) => ({
			...prev,
			pageIndex: 0,
		}));
	}, []);

	// -------------------------------------------------------------------------
	// Toggle ship selection
	// -------------------------------------------------------------------------
	const handleToggleSelect = useCallback(
		(ship) => {
			if (!isEditable) return;

			const key = vesselKey(ship);
			const currentSelection = selectedShipMap[key];

			const newMap = {
				...selectedShipMap,
			};

			if (currentSelection === ship.reportMstId) {
				delete newMap[key];
			} else {
				newMap[key] = ship.reportMstId;
			}

			setSelectedShipMap(newMap);
			setUnselectedWarning(null);
		},
		[selectedShipMap, isEditable]
	);

	// -------------------------------------------------------------------------
	// Select All — fetches ALL eligible ship IDs across all pages from backend
	// -------------------------------------------------------------------------
	const handleSelectAll = useCallback(async () => {
		if (!isEditable) return;

		setIsSelectingAll(true);
		try {
			const allIds = await getUKETSAllEligibleShipIds(ukEtsAerMstId);

			if (!allIds || allIds.length === 0) {
				emitToasterMsg("warning", "No Ships", "No eligible ships found to select.");
				return;
			}

			const newMap = {};
			allIds.forEach(({ key, reportMstId }) => {
				newMap[key] = reportMstId;
			});

			setSelectedShipMap(newMap);
			setUnselectedWarning(null);
		} catch {
			emitToasterMsg("error", "Error", "Failed to select all ships.");
		} finally {
			setIsSelectingAll(false);
		}
	}, [ukEtsAerMstId, isEditable]);

	// -------------------------------------------------------------------------
	// Clear All
	// -------------------------------------------------------------------------
	const handleClearAll = useCallback(() => {
		if (!isEditable) return;

		setSelectedShipMap({});
		setUnselectedWarning(null);
	}, [isEditable]);

	// -------------------------------------------------------------------------
	// Save
	// -------------------------------------------------------------------------
	const handleSave = async () => {
		if (!isEditable || isSaving) return;

		const aerReportIds = Object.values(selectedShipMap);

		if (aerReportIds.length === 0) {
			emitToasterMsg("warning", "Selection Required", "Please select at least one ship.");
			return;
		}

		// Compute unselected vessels (eligible ships whose key is not in map)
		const unselected = verifiedShips.filter((ship) => {
			const key = vesselKey(ship);
			return !(key in selectedShipMap);
		});

		if (unselected.length > 0) {
			const imoList = [...new Set(
				unselected
					.map((s) => s.imoNumber)
					.filter(Boolean)
			)].join(", ");
			setUnselectedWarning(
				`You have missed selecting vessel(s) with IMO number(s): ${imoList}`
			);
		} else {
			setUnselectedWarning(null);
		}

		try {
			setIsSaving(true);

			const result = await saveUKETSAerReports(Number(ukEtsAerMstId), aerReportIds, false);

			if (result?.isSuccess) {
				emitToasterMsg("success", "Success", "Ships saved successfully.");
			} else {
				emitToasterMsg("error", "Save Failed", result?.errorMessage || "Failed to save ships.");
			}
		} catch (err) {
			emitToasterMsg("error", "Error", err.message);
		} finally {
			setIsSaving(false);
		}
	};

	// -------------------------------------------------------------------------
	// AER Columns
	// -------------------------------------------------------------------------
	const aerColumns = useMemo(
		() =>
			buildAerColumns(selectedShipMap, handleToggleSelect, isEditable, (ship) => {

				setSelectedShipForDrawer(ship);

				setDrawerOpen(true);
			}),
		[selectedShipMap, handleToggleSelect, isEditable]
	);

	const loading = allShipsLoading || eligibleShipsLoading;

	const allShipsTable = useMaterialReactTable({
		columns: ALL_SHIPS_COLUMNS,
		data: allShips ?? [],

		enableStickyHeader: true,
		enablePagination: true,
		manualPagination: true,

		rowCount: allShipsRowCount,

		onPaginationChange: setAllShipsPagination,

		state: {
			isLoading: allShipsLoading,
			pagination: allShipsPagination,
		},

		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		enableTopToolbar: false,
		enableBottomToolbar: true,
        enableColumnResizing: false,
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},

		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
			},
		},

		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},

		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},

		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 343px)",
				overflowY: allShips?.length ? "auto" : "hidden",
			},
		},
	});
	const eligibleShipsTable = useMaterialReactTable({
		columns: aerColumns,
		data: verifiedShips ?? [],

		enableStickyHeader: true,
		enablePagination: true,
		manualPagination: true,

		rowCount: eligibleShipsRowCount,

		onPaginationChange: setEligibleShipsPagination,

		state: {
			isLoading: eligibleShipsLoading,
			pagination: eligibleShipsPagination,
		},

		enableSorting: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		enableTopToolbar: false,
		enableBottomToolbar: true,     
	    enableColumnResizing: false,
		muiPaginationProps: {
			rowsPerPageOptions: [25, 50, 75, 100],
			showFirstButton: true,
			showLastButton: true,
		},

		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",

				"& .MuiButtonBase-root": {
					color: "white !important",
				},
			},
		},

		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},
			},
		},

		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},

		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 343px)",
				overflowY: verifiedShips?.length ? "auto" : "hidden",
			},
		},
	});
	// -------------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------------
	return (
		<div className="p-3" style={{ position: "relative" }}>
			{/* ---------------------------------------------------------------
				Section 1: All Ships
			--------------------------------------------------------------- */}
			<div className="mb-4">
				<div
					style={{
						border: "1px solid #e0e0e0",
						backgroundColor: "#fff",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "10px 16px",
							backgroundColor: "#fff",
							color: "#042a4c",
						}}
					>
						<span
							style={{
								fontWeight: "500",
								fontSize: "16px",
							}}
						>
							All Ships
						</span>

						<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
							<Collapse in={showAllShipsSearch} timeout="auto" unmountOnExit orientation="horizontal">
								<GlobalFilterTextField
									searchInput={allShipsSearchInput}
									applyGlobalSearch={applyAllShipsSearch}
									ref={allShipsSearchRef}
									autoFocus={showAllShipsSearch}
								/>
							</Collapse>

							<IconButton onMouseDown={(e) => e.preventDefault()} onClick={() => setShowAllShipsSearch((v) => !v)}>
								{showAllShipsSearch ? <SearchOff /> : <SearchIcon />}
							</IconButton>
						</div>
					</div>

					<MaterialReactTable table={allShipsTable} />
				</div>
			</div>

			{/* ---------------------------------------------------------------
				Section 2: Ships in UK ETS AER
			--------------------------------------------------------------- */}
			<div
				style={{
					border: "1px solid #e0e0e0",
					borderBottom: "none",
				}}
			>
				<div
					style={{
						backgroundColor: "#fff",
						color: "#042a4c",
						padding: "8px 16px",
						display: "flex",
						alignItems: "center",
						gap: "12px",
					}}
				>
					<span
						style={{
							fontWeight: "500",
							fontSize: "16px",
							whiteSpace: "nowrap",
						}}
					>
						Ships in the UK ETS AER
					</span>

					<div
						style={{
							marginLeft: "auto",
							display: "flex",
							alignItems: "center",
							gap: "4px",
						}}
					>
						<Collapse in={showEligibleShipsSearch} timeout="auto" unmountOnExit orientation="horizontal">
							<GlobalFilterTextField
								searchInput={eligibleShipsSearchInput}
								applyGlobalSearch={applyEligibleShipsSearch}
								ref={eligibleShipsSearchRef}
								autoFocus={showEligibleShipsSearch}
							/>
						</Collapse>

						<IconButton onMouseDown={(e) => e.preventDefault()} onClick={() => setShowEligibleShipsSearch((v) => !v)}>
							{showEligibleShipsSearch ? <SearchOff /> : <SearchIcon />}
						</IconButton>

						{isEditable && (
							<Button
								
								onClick={handleSelectAll}
								disabled={isSaving || isSelectingAll || verifiedShips.length === 0}
								style={{
									
									whiteSpace: "nowrap",
								}}
							>
								{isSelectingAll ? "Selecting..." : "Select All"}
							</Button>
						)}

						{isEditable && (
							<Button
								variant="secondary"
								onClick={handleClearAll}
								disabled={isSaving || Object.keys(selectedShipMap).length === 0}
								style={{
									color: "#fff",
									borderColor: "#fff",
									whiteSpace: "nowrap",
								}}
							>
								Clear
							</Button>
						)}
					</div>
				</div>
			</div>

			<div
				style={{
					border: "1px solid #e0e0e0",
					borderTop: "none",
				}}
			>
				<MaterialReactTable table={eligibleShipsTable} />

				{isEditable && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: "20px",
							margin: "10px 10px",
							padding: "14px 0",
						}}
					>
						{unselectedWarning ? (
							<div
								style={{
									display: "flex",
									alignItems: "flex-start",
									gap: "10px",
									flex: "1",
									padding: "10px 14px",
									backgroundColor: "#fff8e1",
									border: "1px solid #f0b429",
									borderLeft: "4px solid #f0b429",
									borderRadius: "4px",
									color: "#7a4f01",
									fontSize: "13px",
									lineHeight: "1.5",
									minWidth: 0,
								}}
							>
								<span
									style={{
										fontSize: "16px",
										lineHeight: "1.2",
										flexShrink: 0,
									}}
								>
									⚠️
								</span>

								<span>{unselectedWarning}</span>
							</div>
						) : (
							<div style={{ flex: "1" }} />
						)}

						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "12px",
								flexShrink: 0,
							}}
						>
							<Button variant="primary" onClick={handleSave} disabled={isSaving || Object.keys(selectedShipMap).length === 0}>
								{isSaving ? "Saving..." : "Save"}
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* ---------------------------------------------------------------
				Ship-Level Emission Report Drawer
			--------------------------------------------------------------- */}
			{drawerOpen && selectedShipForDrawer && (
				<ShipLevelDrawer
					isOpen={drawerOpen}
					ukEtsAerMstId={ukEtsAerMstId}
					ship={selectedShipForDrawer}
					status={status}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedShipForDrawer(null);
					}}
				/>
			)}
		</div>
	);
};

export default UKETSListOfShips;
